import React, { memo, useEffect, useMemo, useRef, useState } from "react"
import { makeStyles } from "tss-react/mui"
import { IconProp } from "@fortawesome/fontawesome-svg-core"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Box, Grid, ToggleButton, ToggleButtonGroup } from "@mui/material"
import { LocalizedEnum } from "../../../../modules/localization/components/AppLocalized"
import { faSpinnerThird } from "@fortawesome/pro-light-svg-icons/faSpinnerThird"
import { theme } from "../../../../modules/theme/theme"

const LOADING_TIMEOUT = 1000

type StyleProps = Readonly<{
    isLight: boolean
    isMarginBottom: boolean
}>

const useStyles = makeStyles<StyleProps>()((theme, { isLight, isMarginBottom }) => ({
    container: {
        padding: ".25rem",
        borderRadius: "5.625rem",
        backgroundColor: isLight ? theme.palette.common.white : theme.palette.tailwind.slate[100],
        marginBottom: isMarginBottom ? "1.5rem" : "0",
        border: `1px solid ${theme.palette.grey[300]}`,
    },
    wrap: {
        position: "relative",
        display: "flex",
        alignItems: "center",
        overflow: "hidden",
    },
    toggleGroup: {
        gap: ".5rem",
        "& .MuiToggleButtonGroup-grouped:not(:last-of-type), & .MuiToggleButtonGroup-grouped:not(:first-of-type)": {
            borderRadius: "5.625rem",
        },
    },
    toggleButton: {
        position: "relative",
        zIndex: 1,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        gap: "0.375rem",
        textTransform: "none",
        border: "none",
        padding: ".5rem 1rem",
        fontWeight: 400,
        color: theme.palette.text.secondary,
        transition: "all 0.2s ease",
        background: "transparent",

        "&.Mui-selected, &.Mui-selected: hover": {
            fontWeight: 500,
            background: "transparent",
        },
        "&:hover": {
            background: "transparent",
        },
    },
    highlight: {
        position: "absolute",
        top: 0,
        left: 0,
        height: "100%",
        borderRadius: "5.625rem",
        transition: "all 0.3s ease",
        zIndex: 0,
    },
    buttonGreen: {
        "&:hover": {
            background: theme.palette.tailwind.green[200],
        },
        "&.Mui-selected": {
            // backgroundColor: theme.palette.tailwind.green[300],
            color: theme.palette.tailwind.green[900],
        },
    },
    buttonTeal: {
        "&:hover": {
            background: theme.palette.tailwind.teal[200],
        },
        "&.Mui-selected": {
            // backgroundColor: theme.palette.tailwind.green[300],
            color: theme.palette.tailwind.teal[900],
        },
    },
    buttonBlue: {
        "&:hover": {
            background: theme.palette.tailwind.blue[100],
        },
        "&.Mui-selected": {
            // backgroundColor: theme.palette.tailwind.blue[300],
            color: theme.palette.tailwind.blue[900],
        },
    },
    buttonPurple: {
        "&:hover": {
            background: theme.palette.tailwind.purple[100],
        },
        "&.Mui-selected": {
            // backgroundColor: theme.palette.tailwind.purple[300],
            color: theme.palette.tailwind.purple[900],
        },
    },
    buttonAmber: {
        "&:hover": {
            background: theme.palette.tailwind.amber[100],
        },
        "&.Mui-selected": {
            color: theme.palette.tailwind.amber[900],
        },
    },
}))

export type ButtonConfigType<T extends string> = Readonly<{
    value: T
    icon: IconProp | null
    className: keyof ReturnType<typeof useStyles>["classes"]
    disabled?: boolean
}>

type StandardOrderTypeFilterProps<T extends string> = Readonly<{
    orderType: T
    setOrderType: (value: T) => void
    config: ButtonConfigType<T>[]
    isLight?: boolean
    isMarginBottom?: boolean
}>

function StandardOrderTypeFilterInner<T extends string>({
    orderType,
    setOrderType,
    config,
    isLight = true,
    isMarginBottom = true,
}: StandardOrderTypeFilterProps<T>) {
    const { classes, cx } = useStyles({ isLight, isMarginBottom })
    const containerRef = useRef<HTMLDivElement>(null)
    const timerRef = useRef<NodeJS.Timeout | null>(null)
    const [highlightStyle, setHighlightStyle] = useState<React.CSSProperties>({
        left: 0,
        width: 0,
        backgroundColor: theme.palette.primary.main,
    })

    const [loadingButton, setLoadingButton] = useState<T | null>(null)

    useEffect(() => {
        if (!loadingButton) return

        if (loadingButton === orderType) {
            setLoadingButton(null)
            return
        }

        if (timerRef.current) {
            clearTimeout(timerRef.current)
        }

        timerRef.current = setTimeout(() => {
            setLoadingButton(null)
        }, LOADING_TIMEOUT)

        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current)
            }
        }
    }, [orderType, loadingButton])

    useEffect(() => {
        const updateHighlight = () => {
            if (containerRef.current) {
                const buttons = containerRef.current.querySelectorAll(".MuiToggleButton-root")
                const activeButton = Array.from(buttons).find((button) => button.getAttribute("value") === orderType)
                if (activeButton) {
                    const { offsetLeft, offsetWidth } = activeButton as HTMLElement
                    const selectedConfig = config.find(({ value }) => value === orderType)

                    let backgroundColor = theme.palette.tailwind.purple[200] // Default color

                    if (selectedConfig?.className === "buttonGreen") {
                        backgroundColor = theme.palette.tailwind.green[200]
                    } else if (selectedConfig?.className === "buttonBlue") {
                        backgroundColor = theme.palette.tailwind.blue[200]
                    } else if (selectedConfig?.className === "buttonTeal") {
                        backgroundColor = theme.palette.tailwind.teal[200]
                    } else if (selectedConfig?.className === "buttonPurple") {
                        backgroundColor = theme.palette.tailwind.purple[200]
                    } else if (selectedConfig?.className === "buttonAmber") {
                        backgroundColor = theme.palette.tailwind.amber[200]
                    }

                    setHighlightStyle({
                        left: offsetLeft,
                        width: offsetWidth,
                        backgroundColor,
                    })
                }
            }
        }

        const observer = new ResizeObserver(updateHighlight)
        if (containerRef.current) {
            observer.observe(containerRef.current)
        }

        updateHighlight()

        return () => {
            if (containerRef.current) {
                observer.unobserve(containerRef.current)
            }
        }
    }, [orderType, config])

    const handleChange = (_event: React.MouseEvent<HTMLElement>, newValue: T) => {
        if (newValue !== null && newValue !== orderType) {
            setLoadingButton(newValue)
            setOrderType(newValue)
        }
    }

    const filteredConfig = useMemo(() => config.filter(({ disabled }) => !disabled), [config])

    return (
        <Grid container justifyContent="flex-start" alignItems="center">
            <Box className={classes.container}>
                <Box ref={containerRef} className={classes.wrap}>
                    <Box className={classes.highlight} style={highlightStyle} />
                    <ToggleButtonGroup
                        value={orderType}
                        exclusive
                        onChange={handleChange}
                        className={classes.toggleGroup}
                    >
                        {filteredConfig.map(({ value, icon, className }) => (
                            <ToggleButton
                                key={String(value)}
                                value={value}
                                className={cx(classes.toggleButton, className && classes[className])}
                                disableRipple
                            >
                                {loadingButton === value ? (
                                    <FontAwesomeIcon icon={faSpinnerThird} spin />
                                ) : (
                                    icon && orderType === value && <FontAwesomeIcon icon={icon} />
                                )}

                                <LocalizedEnum base="standard-order-type-filter-mode" enumValue={value}>
                                    <> {value}</>
                                </LocalizedEnum>
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                </Box>
            </Box>
        </Grid>
    )
}

export const StandardOrderTypeFilter = memo(StandardOrderTypeFilterInner) as typeof StandardOrderTypeFilterInner
